/**
 * Compound Press — CSS marquee + Swiper slider.
 *
 * Marquee: fill the viewport with one logo set, duplicate that set once,
 * animate translateX(-50%). Works for few/many logos and all breakpoints.
 *
 * [data-compound-press]
 *   data-autoplay / data-autoplay-delay
 *   data-ticker-speed (px/sec, default 48)
 *   data-pagination-style: light | dark
 */
(() => {
  const SECTION_SELECTOR = '[data-compound-press]';

  function prefersReducedMotion() {
    return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ── Swiper ─────────────────────────────────────────────── */

  function ensureSwiper(cb) {
    if (typeof window.Swiper === 'function') return cb();

    let settled = false;
    let intervalId;
    const finish = () => {
      if (settled) return;
      if (typeof window.Swiper !== 'function') return;
      settled = true;
      if (intervalId) clearInterval(intervalId);
      cb();
    };

    const existing = document.querySelector('script[data-swiper-asset]');
    if (existing) existing.addEventListener('load', finish, { once: true });

    intervalId = setInterval(finish, 50);
    setTimeout(() => {
      if (intervalId) clearInterval(intervalId);
    }, 12000);
  }

  function loadSwiperScript() {
    if (typeof window.Swiper === 'function') return;
    if (document.querySelector('script[data-swiper-asset]')) return;

    const url = window.__compoundSwiperBundleUrl;
    if (!url) return;

    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    script.defer = true;
    script.dataset.swiperAsset = 'true';
    document.head.appendChild(script);
  }

  /* ── Marquee ──────────────────────────────────────────────
   * Pixel translate (not %). iOS treats % of max-content as 0.
   */

  function stopMarquee(track) {
    if (track && track.__pressAnim) {
      try {
        track.__pressAnim.cancel();
      } catch (_) {}
      track.__pressAnim = null;
    }
    if (track) track.style.transform = '';
  }

  function waitForImages(root) {
    const imgs = Array.from(root.querySelectorAll('[data-press-marquee-group] img'));
    if (!imgs.length) return Promise.resolve();

    const ready = Promise.all(
      imgs.map(
        (img) =>
          img.complete
            ? Promise.resolve()
            : new Promise((resolve) => {
                img.addEventListener('load', resolve, { once: true });
                img.addEventListener('error', resolve, { once: true });
              })
      )
    );
    return Promise.race([ready, new Promise((resolve) => setTimeout(resolve, 500))]);
  }

  function setWidth(group) {
    return Math.ceil(group.getBoundingClientRect().width || group.scrollWidth || 0);
  }

  function buildMarquee(sectionEl) {
    const viewport = sectionEl.querySelector('[data-press-marquee]');
    const track = sectionEl.querySelector('[data-press-marquee-track]');
    const group = sectionEl.querySelector('[data-press-marquee-group]');
    if (!viewport || !track || !group) return;

    if (!group.dataset.pressSource) group.dataset.pressSource = group.innerHTML;

    stopMarquee(track);
    track.querySelectorAll('[data-press-marquee-clone]').forEach((el) => el.remove());
    group.innerHTML = group.dataset.pressSource;

    if (!group.dataset.pressSource.trim()) return;

    const viewW = Math.ceil(viewport.getBoundingClientRect().width || viewport.offsetWidth || 0);
    if (viewW <= 0) return;

    let loops = 0;
    while (setWidth(group) < viewW && loops < 12) {
      group.insertAdjacentHTML('beforeend', group.dataset.pressSource);
      loops += 1;
    }

    const shift = setWidth(group);
    if (shift <= 0) return;

    const clone = group.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('data-press-marquee-clone', '');
    track.appendChild(clone);

    const speed = Math.max(parseFloat(sectionEl.dataset.tickerSpeed) || 48, 12);
    const duration = Math.max((shift / speed) * 1000, 8000);

    if (typeof track.animate === 'function') {
      track.__pressAnim = track.animate(
        [{ transform: 'translate3d(0,0,0)' }, { transform: 'translate3d(' + -shift + 'px,0,0)' }],
        { duration: duration, iterations: Infinity, easing: 'linear' }
      );
    }

    sectionEl.__pressMarqueeWidth = viewW;
  }

  function bindMarquee(sectionEl) {
    const viewport = sectionEl.querySelector('[data-press-marquee]');
    if (!viewport) return;

    let timer;
    const rebuild = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const w = Math.ceil(viewport.getBoundingClientRect().width || 0);
        if (w > 0 && Math.abs((sectionEl.__pressMarqueeWidth || 0) - w) < 2) return;
        buildMarquee(sectionEl);
      }, 200);
    };

    window.addEventListener('resize', rebuild);
    sectionEl.__pressMarqueeOnResize = rebuild;

    if (typeof IntersectionObserver === 'function') {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const track = sectionEl.querySelector('[data-press-marquee-track]');
          if (track && track.__pressAnim) return;
          buildMarquee(sectionEl);
        });
      });
      io.observe(viewport);
      sectionEl.__pressMarqueeIO = io;
    }
  }

  function destroyMarquee(sectionEl) {
    const track = sectionEl.querySelector('[data-press-marquee-track]');
    stopMarquee(track);
    if (sectionEl.__pressMarqueeOnResize) {
      window.removeEventListener('resize', sectionEl.__pressMarqueeOnResize);
      sectionEl.__pressMarqueeOnResize = null;
    }
    if (sectionEl.__pressMarqueeIO) {
      try {
        sectionEl.__pressMarqueeIO.disconnect();
      } catch (_) {}
      sectionEl.__pressMarqueeIO = null;
    }
  }

  /* ── Slider ─────────────────────────────────────────────── */

  function destroyPressSwiper(sectionEl) {
    if (sectionEl.__pressSwiper) {
      try {
        sectionEl.__pressSwiper.destroy(true, true);
      } catch (_) {}
      sectionEl.__pressSwiper = null;
    }
    if (sectionEl.__pressSwiperIO) {
      try {
        sectionEl.__pressSwiperIO.disconnect();
      } catch (_) {}
      sectionEl.__pressSwiperIO = null;
    }
  }

  function initPressSwiper(sectionEl) {
    destroyPressSwiper(sectionEl);

    const swiperEl = sectionEl.querySelector('[data-press-swiper]');
    const paginationEl = sectionEl.querySelector('[data-press-pagination]');
    if (!swiperEl) return;

    const slideCount = swiperEl.querySelectorAll('.swiper-slide').length;
    if (!slideCount) return;

    const reduced = prefersReducedMotion();
    const autoplayOn = sectionEl.dataset.autoplay === 'true' && slideCount > 1 && !reduced;
    const autoplayDelay = Math.max(parseInt(sectionEl.dataset.autoplayDelay, 10) || 5000, 1500);

    if (paginationEl) {
      paginationEl.classList.toggle('c-press__pagination--on-dark', sectionEl.dataset.paginationStyle === 'dark');
    }

    ensureSwiper(() => {
      if (typeof window.Swiper !== 'function' || !sectionEl.isConnected) return;

      sectionEl.__pressSwiper = new window.Swiper(swiperEl, {
        effect: 'fade',
        fadeEffect: { crossFade: true },
        speed: reduced ? 0 : 520,
        loop: slideCount > 1,
        watchOverflow: true,
        allowTouchMove: slideCount > 1,
        observer: true,
        observeParents: true,
        keyboard: slideCount > 1 ? { enabled: true, onlyInViewport: true } : false,
        a11y:
          slideCount > 1
            ? { enabled: true, paginationBulletMessage: 'Go to press slide {{index}}' }
            : { enabled: false },
        pagination:
          slideCount > 1 && paginationEl
            ? { el: paginationEl, clickable: true, bulletClass: 'c-press__bullet', bulletActiveClass: 'is-active' }
            : false,
        autoplay: autoplayOn
          ? { delay: autoplayDelay, disableOnInteraction: false, pauseOnMouseEnter: true }
          : false,
      });

      if (autoplayOn && sectionEl.__pressSwiper.autoplay && typeof IntersectionObserver !== 'undefined') {
        const io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!sectionEl.__pressSwiper || !sectionEl.__pressSwiper.autoplay) return;
              if (entry.isIntersecting) sectionEl.__pressSwiper.autoplay.start();
              else sectionEl.__pressSwiper.autoplay.stop();
            });
          },
          { threshold: 0.2 }
        );
        io.observe(sectionEl);
        sectionEl.__pressSwiperIO = io;
      }
    });
  }

  /* ── Boot ───────────────────────────────────────────────── */

  function destroyPressSection(sectionEl) {
    if (!sectionEl) return;
    destroyPressSwiper(sectionEl);
    destroyMarquee(sectionEl);
  }

  function initPressSection(sectionEl) {
    if (!sectionEl) return;
    destroyPressSection(sectionEl);

    waitForImages(sectionEl).then(() => {
      if (!sectionEl.isConnected) return;
      buildMarquee(sectionEl);
      bindMarquee(sectionEl);
    });

    window.addEventListener(
      'load',
      () => {
        if (sectionEl.isConnected) buildMarquee(sectionEl);
      },
      { once: true }
    );

    loadSwiperScript();
    initPressSwiper(sectionEl);
  }

  function bootAll() {
    document.querySelectorAll(SECTION_SELECTOR).forEach(initPressSection);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bootAll);
  else bootAll();

  document.addEventListener('shopify:section:load', (e) => {
    const sectionEl =
      (e.target && e.target.querySelector && e.target.querySelector(SECTION_SELECTOR)) ||
      (e.target && e.target.matches && e.target.matches(SECTION_SELECTOR) ? e.target : null);
    if (sectionEl) initPressSection(sectionEl);
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const sectionEl =
      (e.target && e.target.querySelector && e.target.querySelector(SECTION_SELECTOR)) ||
      (e.target && e.target.matches && e.target.matches(SECTION_SELECTOR) ? e.target : null);
    if (sectionEl) destroyPressSection(sectionEl);
  });
})();
